"use client";

/**
 * Controls, and the two chrome switches. Each reads the one context; none takes
 * a `compact` prop, because the two surfaces compose different parts rather
 * than passing a flag down through one.
 */
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { Maximize2, Mic, MicOff, Minimize2, MonitorUp, PhoneOff, Users, Video, VideoOff } from "lucide-react";
import type { ReactNode } from "react";
import { useCall } from "./context";

/**
 * The bottom bar: who you are with, what you can switch off, and the door.
 *
 * A SOLID BAR RATHER THAN BUTTONS FLOATING ON THE VIDEO. Floating controls have
 * to carry their own contrast — the gradient behind them exists only to stop a
 * white shirt swallowing the mute button — and they still sit on top of the
 * person you are talking to. A bar owns its own strip, so the video is never
 * partly covered and the controls are legible against anything.
 *
 * Three regions, and the outer two are equal-width so the controls stay centred
 * on the VIDEO rather than drifting with whatever the side regions hold. The
 * left one is empty today and stays in the markup for exactly that reason: it
 * is the counterweight, not a gap waiting to be filled.
 *
 * The title is NOT here — it is in the top strip, where Meet puts it. One name
 * in one place, and the bar is only the things you press.
 */
export function CallControls() {
  const { muted, cameraOn, sharing, canShareScreen, waiting, hasWaitingRoom, geometry, actions } = useCall();

  return (
    <div className="absolute inset-x-0 bottom-0 z-10 flex h-14 items-center gap-3 bg-muted px-3">
      <div className="min-w-0 flex-1" />

      <div className="flex items-center gap-2.5">
        <ControlButton tone={muted ? "danger" : "idle"} onClick={actions.toggleMute} label="Microphone">
          {muted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
        </ControlButton>
        <ControlButton tone={cameraOn ? "idle" : "danger"} onClick={actions.toggleCamera} label="Camera">
          {cameraOn ? <Video className="size-5" /> : <VideoOff className="size-5" />}
        </ControlButton>
        {canShareScreen && (
          <ControlButton tone={sharing ? "sharing" : "idle"} onClick={actions.toggleShare} label="Share screen">
            <MonitorUp className="size-5" />
          </ControlButton>
        )}
        <Button
          onClick={actions.leave}
          aria-label="Leave call"
          title="Leave call"
          className="ml-3 h-12 w-16 rounded-full border-transparent bg-red-500 text-white hover:bg-red-400"
        >
          <PhoneOff className="size-5" />
        </Button>
      </div>

      {/*
       * The panel toggle belongs HERE, beside the other things you switch, not
       * on a tab welded to the panel's own edge — an edge that moves when the
       * panel does. A control that relocates itself when used is one the hand
       * has to find again every time.
       */}
      <div className="flex flex-1 items-center justify-end">
        {hasWaitingRoom && (
          <Button
            variant="outline"
            size="icon-lg"
            onClick={actions.toggleAside}
            aria-expanded={geometry.asideOpen}
            aria-label={geometry.asideOpen ? "Hide the waiting room" : "Show the waiting room"}
            title={geometry.asideOpen ? "Hide the waiting room" : "Show the waiting room"}
            className="relative size-11 rounded-full"
          >
            <Users className="size-5" />
            {waiting.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid size-5 place-items-center rounded-full bg-amber-400 text-[10px] font-bold text-black">
                {waiting.length}
              </span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

/** The minimised bar: no screen share, no chat, smaller targets. */
export function CallCompactControls() {
  const { muted, cameraOn, actions } = useCall();

  return (
    <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 p-2">
      <PipButton onClick={actions.toggleMute} label="Microphone" danger={muted}>
        {muted ? <MicOff className="size-3.5" /> : <Mic className="size-3.5" />}
      </PipButton>
      <PipButton onClick={actions.toggleCamera} label="Camera" danger={!cameraOn}>
        {cameraOn ? <Video className="size-3.5" /> : <VideoOff className="size-3.5" />}
      </PipButton>
      <PipButton onClick={actions.leave} label="Leave call" danger solid>
        <PhoneOff className="size-3.5" />
      </PipButton>
    </div>
  );
}

/**
 * Sits in the top strip, not on the video — so it wears the strip's colours
 * rather than the half-black disc an overlaid control needs to stay visible
 * against whatever is behind it.
 */
export function CallMinimise() {
  const { actions } = useCall();
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={actions.minimise}
      aria-label="Minimise call"
      title="Minimise call"
      className="size-8 shrink-0 rounded-full"
    >
      <Minimize2 className="size-4" />
    </Button>
  );
}

export function CallExpand() {
  const { actions } = useCall();
  return (
    <Button
      size="icon-sm"
      onClick={actions.expand}
      aria-label="Expand call"
      title="Expand call"
      className="absolute right-3 top-2.5 size-7 border-transparent bg-black/40 text-white hover:bg-black/60"
    >
      <Maximize2 className="size-3.5" />
    </Button>
  );
}

/** A round call control. White at rest, red when AV is off, blue while sharing. */
function ControlButton({
  children,
  onClick,
  label,
  tone,
}: {
  children: ReactNode;
  onClick: () => void;
  label: string;
  tone: "idle" | "danger" | "sharing";
}) {
  return (
    <Button
      size="icon-lg"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "relative size-12 rounded-full border-transparent",
        tone === "idle" && "bg-white text-neutral-900 hover:bg-neutral-200",
        tone === "danger" && "bg-red-500 text-white hover:bg-red-400",
        tone === "sharing" && "bg-primary text-primary-foreground hover:bg-primary/80",
      )}
    >
      {children}
    </Button>
  );
}

/** A small control that floats over the minimised call's video. */
function PipButton({
  children,
  onClick,
  label,
  danger,
  solid,
}: {
  children: ReactNode;
  onClick?: () => void;
  label: string;
  danger?: boolean;
  solid?: boolean;
}) {
  return (
    <Button
      size="icon-sm"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "size-8 rounded-full border-transparent bg-black/50 text-white backdrop-blur-sm hover:bg-black/70",
        danger && !solid && "bg-red-500/90 hover:bg-red-500",
        solid && "bg-red-500 hover:bg-red-400",
      )}
    >
      {children}
    </Button>
  );
}
